import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ShieldCheck, BookOpen, Link2, Lock } from 'lucide-react';
import { LibraryErrorState, LibraryLoading } from '@/components/forms/LibraryStates';
import { registerError } from '@/lib/auditRegisters';

export default function AuditRegistersHubPage() {
  const [items, setItems] = useState(null); const [error, setError] = useState(null);
  const load = () => { setError(null); api.get('/audit/registers').then(({ data }) => setItems(data.items || [])).catch((e) => setError(registerError(e))); };
  useEffect(load, []);
  const groups = [['Manually maintained registers', (i) => i.kind === 'authored'], ['Derived read-only registers', (i) => i.kind === 'derived'], ['Evidence links', (i) => i.kind === 'link']];
  return (
    <div className="space-y-6" data-testid="audit-registers-hub">
      <header>
        <p className="text-xs uppercase tracking-wide text-muted-foreground">NDIS Audit Readiness Centre</p>
        <h1 className="text-2xl font-semibold flex items-center gap-2"><ShieldCheck className="w-6 h-6" aria-hidden="true" />Audit Registers</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl">Organisation-owned registers and read-only views over authoritative modules. Nothing here claims certification, compliance or audit outcome — registers are working records for your own audit preparation.</p>
      </header>
      {error && <LibraryErrorState error={error} onRetry={load} />}
      {!error && items === null && <LibraryLoading label="Loading registers…" />}
      {items && groups.map(([title, pred]) => {
        const list = items.filter(pred); if (!list.length) return null;
        return (
          <section key={title} aria-labelledby={`hub-${title.replace(/\W+/g, '-')}`}>
            <h2 id={`hub-${title.replace(/\W+/g, '-')}`} className="text-base md:text-lg font-medium mb-3">{title}</h2>
            <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 list-none p-0 m-0">
              {list.map((r) => <li key={r.type}><RegisterCard r={r} /></li>)}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

function RegisterCard({ r }) {
  const href = r.kind === 'link' ? r.link : `/audit/registers/${r.type}`;
  const Icon = r.kind === 'link' ? Link2 : r.read_only ? BookOpen : ShieldCheck;
  const body = (
    <Card className={`h-full transition-shadow ${r.can_view ? 'hover:shadow-md focus-within:shadow-md' : 'opacity-70'}`} data-testid={`register-card-${r.type}`}>
      <CardContent className="p-4 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <span className="font-medium flex items-center gap-2"><Icon className="w-4 h-4" aria-hidden="true" />{r.label}</span>
          <Badge variant={r.read_only ? 'secondary' : 'default'} data-testid={`register-kind-${r.type}`}>{r.kind === 'link' ? 'Link' : r.read_only ? 'Read-only' : 'Manual'}</Badge>
        </div>
        {r.source_module && <p className="text-xs text-muted-foreground">Source: {r.source_module}</p>}
        {r.disclosure && <p className="text-xs rounded border p-2 bg-muted/40" role="note" data-testid={`register-disclosure-${r.type}`}>{r.disclosure}</p>}
        {!r.can_view && <p className="text-xs flex items-center gap-1 text-muted-foreground" data-testid={`register-locked-${r.type}`}><Lock className="w-3 h-3" aria-hidden="true" />Requires {r.source_permission || 'audit_registers.view'}</p>}
      </CardContent>
    </Card>
  );
  if (!r.can_view) return <div aria-disabled="true">{body}</div>;
  return r.kind === 'link' ? <a href={href} className="block rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2" data-testid={`register-link-${r.type}`}>{body}</a>
    : <Link to={href} className="block rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2" data-testid={`register-link-${r.type}`}>{body}</Link>;
}
